import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { TPointDetails } from '@project/view-models';
import { Observable, Subject, of } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { TDropdown } from 'src/app/project/components/dropdown/dropdown.consts';
import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { GET_ACTIVE_PLAN } from 'src/app/project/modules/plan/plan.store';
import { TSiteData } from 'src/app/project/modules/site/site-data.service';
import { GET_TIMELINE } from 'src/app/project/modules/site/site-timeline/timeline.ui.store';
import { TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';
import { EWorkspaces } from 'src/app/project/modules/workspace/workspaces.enum';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { EIconPath } from '../../../shared/enums/icons.enum';
import { SitePointFilterService } from '../../filters/site-point-filter.service';
import { canEditFlag } from '../../share/share-utils/share-permissions';
import { GET_SHARE } from '../../share/shares.store';
import { CustomTableService } from '../../site/site-table/custom-table/custom-table.service';
import { UserService } from '../../user/user.service';
import { PointActivityService } from '../point-modal/point-timeline/point-activity.service';
import { PointsDetailsService } from '../points-details.service';
import { PointsUpdateService } from '../points-update.service';
import { TPoint, TPointsByWorkspace } from '../points.model';
import { PointModalHeaderService } from './point-modal-header.service';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventFlag,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { iconMap } from 'src/app/project/shared/enums/icon-map';

@Component({
  selector: 'pp-point-modal-header',
  templateUrl: './point-modal-header.component.html',
  styleUrls: [
    './point-modal-header.component.scss',
    '../point-full-modal/point-full-modal.component.scss',
  ],
  standalone: false,
})
export class PointModalHeaderComponent implements OnInit {
  @Input() ppFull: boolean;
  @Input() ppPoint: TPoint;
  @Input() ppAttachmentUploading: boolean;
  @Input() ppPointDetails: TPointDetails;
  @Output() ppPointDetailsChange = new EventEmitter<TPointDetails>();
  @Input() ppWorkspaceId: string;
  @Input() ppPointId: string;
  @Output() ppHide = new EventEmitter<void>();
  @Output() ppUpdate = new EventEmitter<void>();
  @Input() ppSite: TSiteData;
  @Input() ppNew: boolean;
  @Input() ppDetailsFetched: boolean;

  workspaces$: Observable<TWorkspacesById>;
  private readonly destroy$ = new Subject<void>();
  private points$: Observable<TPointsByWorkspace>;
  activePlan = GET_ACTIVE_PLAN();
  isTimeline = false;
  isOverview = false;
  hasWorkspaces = false;
  nextPointExists = false;
  private dropdown: TDropdown = this.dropdownService.getDropdown();
  optionsDropdownVisible = false;
  routerPath = this.router.url;
  EIconPath = EIconPath;
  processing = false;
  canEditFlag = false;

  iconMap = iconMap;

  constructor(
    private store: Store<{
      workspaces: TWorkspacesById;
      points: TPointsByWorkspace;
    }>,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private dropdownService: DropdownService,
    private pointsDetailsService: PointsDetailsService,
    private pointModalHeaderService: PointModalHeaderService,
    private customTableService: CustomTableService,
    private pointsUpdateService: PointsUpdateService,
    private translationPipe: TranslationPipe,
    private promptService: PromptService,
    private pointActivityService: PointActivityService,
    private userService: UserService,
    private sitePointFilterService: SitePointFilterService,
  ) {
    this.workspaces$ = this.store.pipe(select(EStore.WORKSPACES));
    this.points$ = this.store.pipe(select(EStore.POINTS));
  }

  ngOnInit(): void {
    this.workspaces$
      .pipe(takeUntil(this.destroy$))
      .subscribe((workspaces) => this.workspacesHandler(workspaces));

    this.points$.pipe(takeUntil(this.destroy$)).subscribe((points) => this.pointsHandler(points));

    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.paramsHandler());

    this.checkIfCanEditFlag();
  }

  workspacesHandler(workspaces: TWorkspacesById): void {
    this.hasWorkspaces = !!workspaces;
  }

  pointsHandler(points: TPointsByWorkspace): void {
    if (Object.keys(points).length > 0) {
      if (this.ppWorkspaceId && points[this.ppWorkspaceId]) {
        if (this.isTimeline) {
          const timeline = GET_TIMELINE();

          this.nextPointExists = timeline?.getPoints().length > 1;
        } else {
          const table = this.customTableService.getTable();

          this.nextPointExists = table?.getPoints().length > 1;
        }
      }
    }
  }

  prevPoint(): void {
    this.pointModalHeaderService.prevPoint({
      attachmentUploading: this.ppAttachmentUploading,
      point: this.ppPoint,
      _id: this.ppPointId,
      isTimeline: this.isTimeline,
      isOverview: this.isOverview,
      isFull: this.ppFull,
    });
  }

  paramsHandler(): void {
    this.isTimeline = this.router.url.search(EWorkspaces.TIMELINE) !== -1;
    this.isOverview = this.router.url.search(EWorkspaces.OVERVIEW) !== -1;

    this.routerPath = this.router.url;
  }

  nextPoint(): void {
    this.pointModalHeaderService.nextPoint({
      attachmentUploading: this.ppAttachmentUploading,
      point: this.ppPoint,
      _id: this.ppPointId,
      isTimeline: this.isTimeline,
      isOverview: this.isOverview,
      isFull: this.ppFull,
    });
  }

  checkIfCanEditFlag(): void {
    const share = GET_SHARE(this.ppWorkspaceId);
    this.canEditFlag = canEditFlag(share);
  }

  handleGTAGEventFlag(flagged: boolean): void {
    if (flagged) {
      logEventInGTAG(EGoogleEventFlag.TOGGLE_FLAG_ON, {
        source: EGoogleEventCategory.POINT_FLAG,
      });
    } else {
      logEventInGTAG(EGoogleEventFlag.TOGGLE_FLAG_OFF, {
        source: EGoogleEventCategory.POINT_FLAG,
      });
    }
  }

  toggleFlag(): void {
    if (this.processing === true || !this.canEditFlag) {
      return;
    }

    this.processing = true;

    const flagged = !this.ppPoint.flagged;

    this.handleGTAGEventFlag(flagged);

    this.pointsUpdateService
      .updatePointField(this.ppPointId, { flagged })
      .pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          const promptText = this.translationPipe.transform('prompt_point_flag_update');
          this.promptService.showSuccess(promptText);
          this.sitePointFilterService.filterPoints({ _keepScrollPosition: true });
          this.pointActivityService.refreshTimeline(this.ppWorkspaceId, this.ppPointId);

          this.ppUpdate.emit();
        }),
        catchError((error) => {
          let promptText = this.translationPipe.transform('prompt_changes_error');

          if (error.status === EStatusCode.FORBIDDEN) {
            promptText = this.translationPipe.transform('prompt_changes_permission_denied');
          }

          this.promptService.showError(promptText);

          return of();
        }),
        finalize(() => {
          this.processing = false;
        }),
      )
      .subscribe();
  }

  viewReminders(): void {
    this.router.navigate(['reminders'], { relativeTo: this.activatedRoute });
  }

  toggleSubscription(): void {
    this.pointsDetailsService
      .togglePointSubscription(this.ppPointId)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.ppPointDetails = this.pointsDetailsService.getPointDetails(this.ppPointId);

          this.ppPointDetailsChange.emit(this.ppPointDetails);
        }),
      )
      .subscribe();
  }

  expandModal(): void {
    if (!this.ppAttachmentUploading && !this.activePlan.active) {
      this.pointModalHeaderService.expandModal({
        attachmentUploading: this.ppAttachmentUploading,
        _id: this.ppPointId,
        workspaceId: this.ppWorkspaceId,
        isTimeline: this.isTimeline,
        isOverview: this.isOverview,
      });
    }
  }

  openInSite(): void {
    const URL = ['/site', this.ppPoint.workspaceRef.id, 'point', this.ppPoint._id];

    this.router.navigate(URL);
  }

  hide(): void {
    this.ppHide.emit();
  }

  toggleModal(): void {
    if (this.ppFull) {
      this.collapseModal();
    } else {
      this.expandModal();
    }
  }

  collapseModal(): void {
    this.pointModalHeaderService.collapseModal({
      attachmentUploading: this.ppAttachmentUploading,
      isTimeline: this.isTimeline,
      isOverview: this.isOverview,
      point: this.ppPoint,
    });
  }

  back(): void {
    this.router.navigate(['.'], { relativeTo: this.activatedRoute });
  }

  isShowingRemindersFullScreen(): boolean {
    return this.router.url.includes('reminders') && this.ppFull;
  }
}
